// src/features/TechnicianAnalysis/TechnicianStatsPage.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, LinearProgress, useTheme
} from '@mui/material';
import {
  Build,
  CheckCircle,
  Warning,
  Schedule,
  Analytics
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { getData } from '../../services/storageService';

const TechnicianStatsPage = ({ user }) => {
  const { t } = useI18n();
  const theme = useTheme();
  const [stats, setStats] = useState({
    totalReports: 0,
    completedTasks: 0,
    pendingTasks: 0,
    maintenanceHours: 0,
    equipmentCount: 0
  });
  const [recentReports, setRecentReports] = useState([]);
  const [topIssues, setTopIssues] = useState([]);

  useEffect(() => {
    loadTechnicianData();
  }, []);

  const loadTechnicianData = async () => {
    try {
      // Завантажуємо технічні звіти
      const techReports = await getData('technician_reports') || [];
      const maintenance = await getData('maintenance') || [];
      const equipment = await getData('equipment') || [];

      // Розрахунок статистики
      const completedTasks = maintenance.filter(task => task.status === 'completed').length;
      const pendingTasks = maintenance.filter(task => task.status === 'pending').length;
      
      setStats({
        totalReports: techReports.length,
        completedTasks,
        pendingTasks,
        maintenanceHours: maintenance.reduce((total, task) => total + (task.hours || 0), 0),
        equipmentCount: equipment.length
      });

      // Останні звіти
      setRecentReports(techReports.slice(-5).reverse());

      // Топ проблем
      const issues = maintenance.reduce((acc, task) => {
        const type = task.type || 'other';
        if (!acc[type]) acc[type] = 0;
        acc[type]++;
        return acc;
      }, {});

      setTopIssues(Object.entries(issues).map(([type, count]) => ({
        type,
        count,
        percentage: (count / maintenance.length) * 100
      })).sort((a, b) => b.count - a.count).slice(0, 5));

    } catch (error) {
      console.error('Error loading technician data:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle sx={{ color: theme.palette.success.main }} />;
      case 'in-progress': return <Schedule sx={{ color: theme.palette.warning.main }} />;
      default: return <Warning sx={{ color: theme.palette.error.main }} />;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('technician_analysis_title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        {t('technician_analysis_subtitle')}
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="primary.main" fontWeight="bold">
              {stats.totalReports}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('technician_total_reports')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="success.main" fontWeight="bold">
              {stats.completedTasks}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('technician_completed_tasks')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="warning.main" fontWeight="bold">
              {stats.pendingTasks}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('technician_pending_tasks')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="info.main" fontWeight="bold">
              {stats.maintenanceHours}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('technician_maintenance_hours')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="secondary.main" fontWeight="bold">
              {stats.equipmentCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('technician_equipment_count')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Reports */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('technician_recent_reports')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('labels_date')}</TableCell>
                      <TableCell>{t('common_status')}</TableCell>
                      <TableCell>{t('common_technician')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentReports.map((report, index) => (
                      <TableRow key={index}>
                        <TableCell>{report.date}</TableCell>
                        <TableCell>
                          <Chip 
                            label={t(`status_${report.status}`)}
                            color={getStatusColor(report.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{report.createdBy}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Issues */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('technician_top_issues')}
              </Typography>
              <Stack spacing={2}>
                {topIssues.map((issue, index) => (
                  <Box key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        {t(`maintlog_event_type_${issue.type}`) || issue.type}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {issue.count}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={issue.percentage}
                      color={index === 0 ? 'error' : index === 1 ? 'warning' : 'primary'}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Equipment Status */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('technician_equipment_status')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                <CheckCircle sx={{ color: 'success.main', fontSize: 40, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">85%</Typography>
                <Typography variant="body2">{t('technician_equipment_operational')}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                <Build sx={{ color: 'warning.main', fontSize: 40, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">12%</Typography>
                <Typography variant="body2">{t('technician_equipment_maintenance')}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                <Warning sx={{ color: 'error.main', fontSize: 40, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">3%</Typography>
                <Typography variant="body2">{t('technician_equipment_critical')}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TechnicianStatsPage;